import React, { useState } from "react";
import { Alert, Badge, Button, Checkbox, Divider, Group, Modal, Stack, Text } from "@mantine/core";
import {
  MULTIPLACER_MODULES_BY_KEY,
  type IMultiplacerModuleKey,
} from "@multiconsult-group/multiplacer-core";
import {
  openapi,
  usePostMultiplacerProjectsUpdateMemberModulesMutation,
  type GetMultiplacerProjectsSingleProjectApiResponse,
} from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectVisibleModulesForCurrentProject } from "../projectSelectors";
import { selectIsSystemAdmin } from "../userSelectors";
import { formatShortDate } from "../globalUtils";

export type ProjectMember = GetMultiplacerProjectsSingleProjectApiResponse["members"][number];
export const memberRoleLabels = {
  OWNER: "Eier",
  ADMIN: "Administrator",
  MEMBER: "Medlem",
  VIEWER: "Leser",
};

export function ProjectMemberDetails({
  member,
  projectNo,
  onClose,
  onSaved,
}: {
  member: ProjectMember;
  projectNo: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isAdmin = useAppSelector(selectIsSystemAdmin);
  const visibleModules = useAppSelector(selectVisibleModulesForCurrentProject);
  const dispatch = useAppDispatch();
  const [save, { isLoading }] = usePostMultiplacerProjectsUpdateMemberModulesMutation();
  const original = member.permissions?.modules.map(({ module }) => module) ?? [];
  const [selected, setSelected] = useState<IMultiplacerModuleKey[]>(original);
  const [error, setError] = useState<string | null>(null);
  // Include assigned modules even if they are currently hidden for this project.
  const moduleKeys = [...new Set([...visibleModules.map(({ key }) => key), ...original])];
  const changed =
    selected.length !== original.length || selected.some((key) => !original.includes(key));

  const onSave = async () => {
    if (!isAdmin || isLoading || !changed) return;
    setError(null);
    const modules = selected.map((module) => ({ module }));
    try {
      await save({ body: { projectNo, userId: String(member.userId), modules } }).unwrap();
    } catch (failure) {
      const status = (failure as { status?: number })?.status;
      setError(
        status === 403
          ? "Du har ikke tilgang til å endre modultilgang."
          : "Kunne ikke lagre modultilgangen. Prøv igjen."
      );
      return;
    }
    // Update both cached project views only after the server has accepted the change.
    dispatch(
      openapi.util.updateQueryData(
        "getMultiplacerProjectsSingleProject",
        { projectNo },
        (project) => {
          const target = project.members.find(
            (entry) => String(entry.userId) === String(member.userId)
          );
          if (target) target.permissions = { ...target.permissions, modules };
        }
      )
    );
    dispatch(
      openapi.util.updateQueryData("getMultiplacerProjects", undefined, (projects) => {
        const target = projects
          .find((project) => project.projectNo === projectNo)
          ?.members.find((entry) => String(entry.userId) === String(member.userId));
        if (target) target.permissions = { ...target.permissions, modules };
      })
    );
    onSaved();
  };

  return (
    <Modal
      opened
      onClose={() => {
        if (!isLoading) onClose();
      }}
      title="Prosjektmedlem"
      size="lg"
      centered
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      withCloseButton={!isLoading}
    >
      <Stack gap="lg">
        <div>
          <Text size="xl" fw={600}>
            {member.displayName || "Navn mangler"}
          </Text>
          <Text c="dimmed">{member.email || "E-post er ikke registrert"}</Text>
        </div>
        <Group justify="space-between">
          <Badge color="gray" variant="light">
            {memberRoleLabels[member.role]}
          </Badge>
          <Text size="sm" c="dimmed">
            Lagt til: {member.addedAt ? formatShortDate(member.addedAt) : "—"}
          </Text>
        </Group>
        <Divider />
        <Stack gap="sm">
          <Text fw={600}>Modultilgang</Text>
          <Text size="sm" c="dimmed">
            {isAdmin
              ? "Velg hvilke moduler brukeren skal ha tilgang til i dette prosjektet."
              : "Kun systemadministratorer kan endre modultilgangen."}
          </Text>
          {isAdmin
            ? moduleKeys.map((key) => (
                <Checkbox
                  key={key}
                  label={MULTIPLACER_MODULES_BY_KEY[key]?.title ?? key}
                  checked={selected.includes(key)}
                  disabled={isLoading}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setSelected((current) =>
                      checked ? [...current, key] : current.filter((module) => module !== key)
                    );
                  }}
                />
              ))
            : original.map((key) => (
                <Text key={key} size="sm">
                  {MULTIPLACER_MODULES_BY_KEY[key]?.title ?? key}
                </Text>
              ))}
          {!selected.length && (
            <Text size="sm" c="dimmed">
              Ingen modultilganger. Brukeren får ikke tilgang til noen moduler.
            </Text>
          )}
        </Stack>
        {error && (
          <Alert color="red" title="Lagring mislyktes">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="neutral" onClick={onClose} disabled={isLoading}>
            {isAdmin ? "Avbryt" : "Lukk"}
          </Button>
          {isAdmin && (
            <Button onClick={onSave} loading={isLoading} disabled={!changed}>
              Lagre modultilgang
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
